import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { SetPasswordDialog } from '@/features/platform-admin/SetPasswordDialog';
import { UserSearch } from '@/features/platform-admin/UserSearch';
import type { PlatformUser } from '@/types/api';

/**
 * Set a user's password directly, for the case the self-service flow cannot
 * reach: a dead mailbox, a person locked out of the address the account was
 * opened with.
 *
 * No email is sent — the operator relays the new password out of band. That is
 * also why it is a separate screen from the clinic list: it is the one action
 * here that amounts to taking over an account, and every use writes an audit row
 * server-side.
 */
export function PlatformPasswordResetPage() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [target, setTarget] = useState<PlatformUser | null>(null);
  const [done, setDone] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-5">
      <header>
        <h1 className="text-2xl font-semibold tracking-[-0.02em]">
          {t('platformAdmin.passwords.title')}
        </h1>
        <p className="mt-1 text-[13.5px] text-muted">{t('platformAdmin.passwords.subtitle')}</p>
      </header>

      {done && (
        <div
          role="status"
          className="rounded-md border border-success/30 bg-success-bg/40 px-3 py-2 text-[12.5px] text-success"
        >
          {t('platformAdmin.passwords.done', { email: done })}
        </div>
      )}

      <UserSearch
        query={query}
        onQueryChange={(value) => {
          setQuery(value);
          setDone(null);
        }}
        label={t('platformAdmin.passwords.searchLabel')}
        placeholder={t('platformAdmin.passwords.searchPlaceholder')}
        action={(user) => (
          <Button
            size="sm"
            variant="secondary"
            aria-label={`${t('platformAdmin.passwords.setPassword')} ${user.email}`}
            onClick={() => {
              setDone(null);
              setTarget(user);
            }}
          >
            {t('platformAdmin.passwords.setPassword')}
          </Button>
        )}
      />

      {target && (
        <SetPasswordDialog
          user={target}
          onClose={() => setTarget(null)}
          onDone={(email) => {
            setTarget(null);
            setDone(email);
          }}
        />
      )}
    </div>
  );
}
